import { useCallback, useEffect, useRef, useState } from "react";
import type { TranscriptTurn } from "@leaply-interview/shared-types";
import {
  finishInterview,
  generateInsights,
  mintAudioUploadUrl,
  mintRealtimeSession,
} from "./api";

/**
 * WebRTC client for the OpenAI Realtime API (architecture §3, §5.1). Ported
 * from `_prototype/lib/useRealtimeAgent.ts` with two structural changes for
 * the NestJS pipeline:
 *
 *   1. Saves the transcript via PATCH `/interviews/:id` (JSON), not the old
 *      multipart POST `/api/interviews`. Audio is uploaded separately via the
 *      `/interviews/:id/audio-url` signed-PUT flow, and the resulting `gs://`
 *      path is included in the PATCH body so the server persists it on the row.
 *
 *   2. Aggregates `response.done.usage` events into a single token-count blob
 *      and forwards it as `usage` in PATCH finish, so the server computes
 *      `cost_usd` per pricing constants (architecture §5.4). Cached vs.
 *      non-cached input is broken out so the pricing function applies the
 *      right rate to each bucket.
 *
 * The hook still:
 *   - Mints an ephemeral OpenAI token via POST `/realtime-sessions`.
 *   - Handles mock mode (no OpenAI key) by generating a dummy "level" signal
 *     so the orb still animates during UI development.
 *   - Records mixed mic + Lea audio with Web Audio + MediaRecorder.
 *   - Pause/resume mutes mic+speaker and cancels the in-flight response.
 *   - Fires-and-forgets the per-interview insights call after finishing.
 */
export type AgentStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "ended"
  | "error";

interface Options {
  studyId: string;
  interviewId: string;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  /** Call-length cap in minutes (study.maxDurationMinutes; default 60 =
   *  OpenAI's hard session limit). A soft wrap-up instruction goes to Lea
   *  1 minute before the cap; `timeUp` flips just before it so the page
   *  auto-ends cleanly instead of letting OpenAI cut the line. */
  maxDurationMinutes?: number;
}

/** Mic constraints: echo cancellation MUST be requested explicitly —
 *  Safari/WebKit doesn't reliably enable it for a bare `{audio: true}`,
 *  and without AEC Lea's own voice from the speakers loops back into the
 *  mic and gets treated as the participant's answer. */
const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

/** Wrap-up instruction sent over the data channel 1 min before the cap. */
const WRAP_UP_INSTRUCTION =
  "The interview time is almost over. Wrap up the conversation NOW: do not ask any new questions. Briefly thank the participant warmly for their time and say a short goodbye, then stop.";

/** Aggregated token totals across all response.done events during a call.
 *  Shape matches `RealtimeUsageAggregate` on the api side (pricing.ts) so the
 *  backend can multiply by per-token rates directly. */
interface UsageAggregate {
  events: number;
  input_text_tokens: number;
  input_audio_tokens: number;
  input_cached_text_tokens: number;
  input_cached_audio_tokens: number;
  output_text_tokens: number;
  output_audio_tokens: number;
}

const ZERO_USAGE: UsageAggregate = {
  events: 0,
  input_text_tokens: 0,
  input_audio_tokens: 0,
  input_cached_text_tokens: 0,
  input_cached_audio_tokens: 0,
  output_text_tokens: 0,
  output_audio_tokens: 0,
};

/** Pick the first recording mime the browser supports. Chrome/Firefox do
 *  webm/opus; Safari (and every iOS browser — they're all WebKit) can ONLY
 *  produce mp4/aac, and `new MediaRecorder(..., {mimeType: "audio/webm"})`
 *  throws there — which used to silently kill recording for those users.
 *  Returns undefined when nothing matches: MediaRecorder then uses the
 *  platform default, which we read back from `recorder.mimeType`. */
function pickRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  return candidates.find((c) => MediaRecorder.isTypeSupported(c));
}

export function useRealtimeAgent({
  studyId,
  interviewId,
  audioRef,
  maxDurationMinutes = 60,
}: Options) {
  const [status, setStatus] = useState<AgentStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [paused, setPaused] = useState(false);
  const [assistantText, setAssistantText] = useState("");
  const [userSpeaking, setUserSpeaking] = useState(false);
  /** Flips to true when the WebRTC peer/data-channel dies mid-call (network
   *  drop, OpenAI closing the session, etc.). The page watches this and
   *  auto-ends the interview so the transcript-so-far is saved instead of
   *  leaving the participant staring at a frozen orb. */
  const [connectionLost, setConnectionLost] = useState(false);
  /** Flips to true at the study's duration cap (after Lea's soft goodbye).
   *  The page watches this and auto-ends the interview. */
  const [timeUp, setTimeUp] = useState(false);
  /** Flips to true when Lea calls the end_interview tool (she said goodbye
   *  and hung up herself). The page watches this and ends via endCall. */
  const [agentEnded, setAgentEnded] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRafRef = useRef<number | null>(null);
  const transcriptRef = useRef<TranscriptTurn[]>([]);
  const partialUserRef = useRef<string>("");
  const partialAssistantRef = useRef<string>("");
  /** Ordered-transcript assembly state (see registerItem/rebuildTranscript):
   *  conversation item_id → arrival-order index + role/text. */
  const itemOrderRef = useRef<Map<string, number>>(new Map());
  const itemsRef = useRef<Map<string, { role: "user" | "assistant"; text: string }>>(
    new Map(),
  );
  const orderCounterRef = useRef(0);
  const mockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  /** Actual mime the recorder negotiated — drives the Blob type and the
   *  signed-URL contentType so Safari mp4 uploads don't 403. */
  const recorderMimeRef = useRef<string>("audio/webm");
  const startedAtRef = useRef<string | null>(null);
  /** Pause accounting so the reported duration is ACTIVE call time, not
   *  wall-clock. `pausedTotalMsRef` accumulates completed pause spans;
   *  `pausedAtRef` holds the start of the current (open) pause, if any. A
   *  participant who pauses and walks away for hours must not inflate the
   *  interview duration. */
  const pausedTotalMsRef = useRef(0);
  const pausedAtRef = useRef<number | null>(null);
  const usageRef = useRef<UsageAggregate>({ ...ZERO_USAGE });
  /** Guards markDead so an intentional teardown (End click / unmount) isn't
   *  misread as a dropped connection. Set true in cleanup, reset in start. */
  const deadRef = useRef(false);
  const deadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Stall watchdog state: when the server commits the user's speech we
   *  expect a response to start. If it doesn't, the agent has silently
   *  stalled (failed response / dropped turn) while the connection looks
   *  healthy — the "answered and then nothing" bug. */
  const awaitingSinceRef = useRef<number | null>(null);
  const nudgeCountRef = useRef(0);
  /** Conversation-idle tracking: covers the "Thanks for that." + silence
   *  stall — the model completes a contentless turn and never continues.
   *  Updated on any speech/response activity; the watchdog re-engages Lea
   *  after 30s of mutual silence (max 2 consecutive times). */
  const lastActivityRef = useRef<number>(0);
  const idleNudgeCountRef = useRef(0);
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Recorder mix destination + current mic source — kept in refs so the
   *  mic-recovery path (alarm/phone-call interruptions) can rewire the mix
   *  to a fresh microphone stream mid-call. */
  const recorderDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);
  /** Duration-cap timers: soft wrap-up at T-60s, hard auto-end at T-5s. */
  const wrapUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hardStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set when Lea calls end_interview; we wait for her goodbye audio to
   *  finish playing (output_audio_buffer.stopped) before flipping
   *  agentEnded, with a timeout fallback. */
  const agentEndPendingRef = useRef(false);
  const agentEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    // Intentional teardown — suppress connection-lost detection from the
    // close events this is about to fire.
    deadRef.current = true;
    if (deadTimerRef.current !== null) {
      clearTimeout(deadTimerRef.current);
      deadTimerRef.current = null;
    }
    if (watchdogRef.current !== null) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (wrapUpTimerRef.current !== null) {
      clearTimeout(wrapUpTimerRef.current);
      wrapUpTimerRef.current = null;
    }
    if (hardStopTimerRef.current !== null) {
      clearTimeout(hardStopTimerRef.current);
      hardStopTimerRef.current = null;
    }
    if (agentEndTimerRef.current !== null) {
      clearTimeout(agentEndTimerRef.current);
      agentEndTimerRef.current = null;
    }
    agentEndPendingRef.current = false;
    awaitingSinceRef.current = null;
    nudgeCountRef.current = 0;
    if (visibilityHandlerRef.current) {
      document.removeEventListener(
        "visibilitychange",
        visibilityHandlerRef.current,
      );
      visibilityHandlerRef.current = null;
    }
    recorderDestRef.current = null;
    micSourceRef.current = null;
    if (mockTimerRef.current !== null) {
      clearInterval(mockTimerRef.current);
      mockTimerRef.current = null;
    }
    if (analyserRafRef.current !== null) {
      cancelAnimationFrame(analyserRafRef.current);
      analyserRafRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (dcRef.current) {
      try {
        dcRef.current.close();
      } catch {
        /* no-op */
      }
      dcRef.current = null;
    }
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch {
        /* no-op */
      }
      pcRef.current = null;
    }
    setLevel(0);
    setPaused(false);
    setAssistantText("");
    setUserSpeaking(false);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  /** Stop MediaRecorder and return the final Blob. Awaited before tearing
   *  down the AudioContext so we capture the full call. */
  const finalizeRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return null;
    return new Promise((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(recorderChunksRef.current, {
          type: recorderMimeRef.current,
        });
        recorderChunksRef.current = [];
        recorderRef.current = null;
        resolve(blob.size > 0 ? blob : null);
      };
      try {
        recorder.stop();
      } catch {
        resolve(null);
      }
    });
  }, []);

  /** Upload the audio blob via the signed PUT URL endpoint, return the
   *  resulting `gs://` path. Retries up to 3 attempts with a short backoff —
   *  a transient network blip used to permanently lose the recording. Still
   *  returns null on final failure; the interview finalizes either way. */
  const uploadAudio = useCallback(
    async (blob: Blob): Promise<string | null> => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          // Re-mint per attempt: cheap, and sidesteps URL-expiry edge cases.
          const { uploadUrl, gcsPath, contentType } = await mintAudioUploadUrl(
            interviewId,
            blob.type || undefined,
          );
          const res = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": contentType },
            body: blob,
          });
          if (res.ok) return gcsPath;
          // eslint-disable-next-line no-console
          console.warn(
            `[audio-upload] PUT failed (attempt ${attempt}/3)`,
            res.status,
            await res.text().catch(() => ""),
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[audio-upload] failed (attempt ${attempt}/3):`, err);
        }
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, attempt * 1000));
        }
      }
      return null;
    },
    [interviewId],
  );

  /** Save transcript + audio + usage via PATCH /interviews/:id. Then
   *  fire-and-forget the insights generation. Returns the server's validity
   *  status (drives reward eligibility on /complete); undefined on failure —
   *  the participant flow continues regardless. */
  const saveInterview = useCallback(
    async (audio: Blob | null): Promise<string | undefined> => {
      try {
        const audioGcsPath = audio ? await uploadAudio(audio) : null;
        const endedAtIso = new Date().toISOString();
        // ACTIVE call duration (the real "interview length"): wall-clock from
        // the call connecting minus any time spent paused. The server still
        // owns the final number — it clamps this to the real window + the
        // study cap — but this is far more accurate than (finish − initiate),
        // which counted the time the link sat open before Begin and any long
        // mid-call pause. End any still-open pause span first.
        const openPauseMs =
          pausedAtRef.current != null ? Date.now() - pausedAtRef.current : 0;
        const startedMs = startedAtRef.current
          ? Date.parse(startedAtRef.current)
          : NaN;
        const activeDurationS = Number.isFinite(startedMs)
          ? Math.max(
              0,
              Math.round(
                (Date.parse(endedAtIso) -
                  startedMs -
                  pausedTotalMsRef.current -
                  openPauseMs) /
                  1000,
              ),
            )
          : undefined;
        const result = await finishInterview(interviewId, {
          transcript: transcriptRef.current,
          endedAt: endedAtIso,
          ...(startedAtRef.current ? { startedAt: startedAtRef.current } : {}),
          ...(activeDurationS != null ? { durationS: activeDurationS } : {}),
          ...(audioGcsPath ? { audioGcsPath } : {}),
          ...(usageRef.current.events > 0
            ? { usage: { ...usageRef.current } }
            : {}),
        });
        // Insights — best-effort, doesn't block.
        generateInsights(interviewId).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[insights] background call failed:", err);
        });
        return result?.status;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[save] failed:", err);
        return undefined;
      }
    },
    [interviewId, uploadAudio],
  );

  // ── Ordered transcript assembly ─────────────────────────────────────────
  // Whisper finishes transcribing the participant AFTER Lea's reply events
  // arrive (the model answers faster than transcription completes), so
  // appending turns in event-ARRIVAL order shuffles the conversation. We
  // instead key every turn by its conversation `item_id` and order by the
  // `conversation.item.created/added` sequence — those fire in true
  // conversation order; the text fills in whenever it lands.
  const registerItem = useCallback(
    (itemId: string, role: "user" | "assistant"): void => {
      if (!itemOrderRef.current.has(itemId)) {
        itemOrderRef.current.set(itemId, orderCounterRef.current++);
        itemsRef.current.set(itemId, { role, text: "" });
      }
    },
    [],
  );

  /** Rebuild transcriptRef from the ordered items. Consecutive same-role
   *  turns merge into one entry — VAD splits a thinking-pause answer into
   *  several commits, but researchers read it as a single reply. */
  const rebuildTranscript = useCallback((): void => {
    const ordered = [...itemsRef.current.entries()]
      .map(([id, item]) => ({
        order: itemOrderRef.current.get(id) ?? Number.MAX_SAFE_INTEGER,
        ...item,
      }))
      .filter((t) => t.text.length > 0)
      .sort((a, b) => a.order - b.order);
    const merged: TranscriptTurn[] = [];
    for (const t of ordered) {
      const last = merged[merged.length - 1];
      if (last && last.role === t.role) {
        last.text = `${last.text} ${t.text}`;
      } else {
        merged.push({ role: t.role, text: t.text });
      }
    }
    transcriptRef.current = merged;
  }, []);

  const handleServerEvent = useCallback((raw: string) => {
    let evt: Record<string, unknown>;
    try {
      evt = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }
    const type =
      typeof evt.type === "string" ? evt.type : "";

    // ── Conversation item ordering (see registerItem above) ─────────────
    if (
      type === "conversation.item.created" ||
      type === "conversation.item.added"
    ) {
      const item = evt.item as
        | { id?: unknown; role?: unknown }
        | undefined;
      if (
        item &&
        typeof item.id === "string" &&
        (item.role === "user" || item.role === "assistant")
      ) {
        registerItem(item.id, item.role);
      }
      return;
    }

    // ── Whisper transcription of the participant ────────────────────────
    if (type.includes("input_audio_transcription")) {
      if (type.endsWith(".delta")) {
        partialUserRef.current +=
          typeof evt.delta === "string" ? evt.delta : "";
        return;
      }
      if (type.endsWith(".completed") || type.endsWith(".done")) {
        const finalText = (
          typeof evt.transcript === "string"
            ? evt.transcript
            : partialUserRef.current
        ).trim();
        partialUserRef.current = "";
        if (finalText.length > 0) {
          const itemId =
            typeof evt.item_id === "string"
              ? evt.item_id
              : `user-fallback-${orderCounterRef.current}`;
          registerItem(itemId, "user");
          itemsRef.current.get(itemId)!.text = finalText;
          rebuildTranscript();
        }
        return;
      }
      return;
    }

    // ── Server-VAD speech boundaries (participant) ───────────────────────
    if (type === "input_audio_buffer.speech_started") {
      lastActivityRef.current = Date.now();
      idleNudgeCountRef.current = 0;
      setUserSpeaking(true);
      return;
    }
    if (
      type === "input_audio_buffer.speech_stopped" ||
      type === "input_audio_buffer.committed"
    ) {
      setUserSpeaking(false);
      if (type === "input_audio_buffer.committed") {
        // Server accepted the user's turn — a response MUST follow. Arm the
        // stall watchdog; response.created disarms it.
        awaitingSinceRef.current = Date.now();
        nudgeCountRef.current = 0;
        lastActivityRef.current = Date.now();
        idleNudgeCountRef.current = 0;
      }
      return;
    }

    // ── Agent response lifecycle ──────────────────────────────────────────
    if (type === "response.created") {
      awaitingSinceRef.current = null;
      nudgeCountRef.current = 0;
      lastActivityRef.current = Date.now();
      // Deliberately do NOT clear assistantText here: the previous caption
      // stays readable until the new response's first words actually arrive
      // (text streams much faster than audio plays — blanking on .created
      // wiped captions the voice hadn't finished saying yet).
      partialAssistantRef.current = "";
      return;
    }

    // Audio transcript deltas (beta vs GA event names — accept both).
    if (
      type.startsWith("response.") &&
      type.includes("audio_transcript.")
    ) {
      if (type.endsWith(".delta") && typeof evt.delta === "string") {
        lastActivityRef.current = Date.now();
        partialAssistantRef.current += evt.delta;
        setAssistantText(partialAssistantRef.current);
        return;
      }
      if (type.endsWith(".done")) {
        const finalText =
          typeof evt.transcript === "string"
            ? evt.transcript.trim()
            : partialAssistantRef.current.trim();
        if (finalText) {
          const itemId =
            typeof evt.item_id === "string"
              ? evt.item_id
              : `assistant-fallback-${orderCounterRef.current}`;
          registerItem(itemId, "assistant");
          itemsRef.current.get(itemId)!.text = finalText;
          rebuildTranscript();
          setAssistantText(finalText);
          partialAssistantRef.current = "";
        }
        return;
      }
    }

    if (
      (type === "response.output_text.delta" ||
        type === "response.text.delta") &&
      typeof evt.delta === "string"
    ) {
      partialAssistantRef.current += evt.delta;
      setAssistantText(partialAssistantRef.current);
      return;
    }

    // ── Usage aggregation (architecture §5.4) ─────────────────────────────
    if (type === "response.done") {
      lastActivityRef.current = Date.now();
      const response = (evt.response ?? {}) as {
        usage?: unknown;
        status?: unknown;
      };
      const u = response.usage as RealtimeUsage | undefined;
      if (u && typeof u === "object") {
        accumulateUsage(usageRef.current, u);
      }
      // A failed/cancelled response means Lea produced no audible turn —
      // re-arm the watchdog so it nudges a fresh response instead of
      // leaving the participant in silence.
      if (response.status === "failed" || response.status === "incomplete") {
        // eslint-disable-next-line no-console
        console.warn("[realtime] response ended with status:", response.status);
        awaitingSinceRef.current = Date.now();
      }
      return;
    }

    // ── Agent-initiated hang-up (end_interview tool) ─────────────────────
    if (
      type === "response.function_call_arguments.done" &&
      (evt.name === "end_interview" ||
        // some event shapes carry the name on the item instead
        evt.name === undefined)
    ) {
      if (evt.name === "end_interview") {
        // Guard against premature / confused tool calls (the reason the tool
        // was briefly disabled): only honour it once the interview is
        // genuinely underway. An early call — e.g. right after "did you get
        // into the app?" — is ignored; the participant can still End by hand.
        const startedMs = startedAtRef.current
          ? Date.parse(startedAtRef.current)
          : Date.now();
        const elapsedMs = Date.now() - startedMs;
        const userTurns = transcriptRef.current.filter(
          (t) => t.role === "user",
        ).length;
        if (elapsedMs < 180_000 || userTurns < 4) {
          // eslint-disable-next-line no-console
          console.warn(
            `[realtime] ignoring early end_interview (elapsed=${Math.round(elapsedMs / 1000)}s, turns=${userTurns})`,
          );
          return;
        }
        // eslint-disable-next-line no-console
        console.info("[realtime] agent called end_interview");
        agentEndPendingRef.current = true;
        // Fallback: end even if the audio-drained event never arrives.
        agentEndTimerRef.current = setTimeout(() => setAgentEnded(true), 8_000);
      }
      return;
    }
    // WebRTC fires this when Lea's audio playback fully drains — the right
    // moment to hang up after her goodbye.
    if (
      type === "output_audio_buffer.stopped" &&
      agentEndPendingRef.current
    ) {
      setAgentEnded(true);
      return;
    }

    if (type === "error" || type === "response.error") {
      // eslint-disable-next-line no-console
      console.warn("[realtime error]", evt);
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setStatus("connecting");
    setConnectionLost(false);
    setTimeUp(false);
    setAgentEnded(false);
    deadRef.current = false;
    startedAtRef.current = new Date().toISOString();
    pausedTotalMsRef.current = 0;
    pausedAtRef.current = null;
    transcriptRef.current = [];
    itemOrderRef.current = new Map();
    itemsRef.current = new Map();
    orderCounterRef.current = 0;
    lastActivityRef.current = Date.now();
    idleNudgeCountRef.current = 0;
    usageRef.current = { ...ZERO_USAGE };

    try {
      const session = await mintRealtimeSession(studyId);

      // Mock mode — simulate a live call so UI work doesn't burn OpenAI tokens.
      if (session.mock) {
        // eslint-disable-next-line no-console
        console.info("[interview] running in mock mode");
        const mockGreeting =
          "Mock greeting — this is a placeholder until the real agent is connected.";
        transcriptRef.current.push({ role: "assistant", text: mockGreeting });
        setAssistantText(mockGreeting);
        mockTimerRef.current = setInterval(() => {
          const t = Date.now() / 600;
          const base = (Math.sin(t) + 1) / 2;
          const jitter = Math.random() * 0.25;
          setLevel(Math.min(1, base * 0.7 + jitter));
        }, 80);
        setStatus("connected");
        return;
      }

      const ephemeralKey = session.client_secret?.value;
      const model = session.model;
      if (!ephemeralKey) throw new Error("missing ephemeral key");

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // ── Dead-connection detection ────────────────────────────────────────
      // OpenAI can close the session server-side (errors, limits) and the
      // network can drop mid-call. Without this the orb just freezes and the
      // participant's answers are never saved.
      const markDead = (reason: string) => {
        if (deadRef.current) return;
        deadRef.current = true;
        // eslint-disable-next-line no-console
        console.warn("[realtime] connection lost:", reason);
        setConnectionLost(true);
      };
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === "failed" || st === "closed") {
          markDead(`peer connection ${st}`);
        } else if (st === "disconnected") {
          // "disconnected" can self-heal — give it a grace window first.
          if (deadTimerRef.current !== null) clearTimeout(deadTimerRef.current);
          deadTimerRef.current = setTimeout(() => {
            deadTimerRef.current = null;
            if (pcRef.current === pc && pc.connectionState === "disconnected") {
              markDead("peer connection disconnected >8s");
            }
          }, 8000);
        } else if (st === "connected" && deadTimerRef.current !== null) {
          clearTimeout(deadTimerRef.current);
          deadTimerRef.current = null;
        }
      };

      pc.ontrack = (e) => {
        const remoteStream = e.streams[0];
        if (audioRef.current) {
          audioRef.current.srcObject = remoteStream ?? null;
        }
        if (!remoteStream) return;
        try {
          const ctx = new AudioContext();
          audioCtxRef.current = ctx;
          // Autoplay policy can spawn the context suspended (ontrack fires
          // outside the user-gesture window) — a suspended context records
          // pure silence. resume() is a no-op when already running.
          if (ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }

          // Analyser on Lea's audio — drives the orb's level meter.
          const remoteSource = ctx.createMediaStreamSource(remoteStream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          remoteSource.connect(analyser);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = ((buf[i] ?? 128) - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            setLevel(Math.min(1, rms * 3));
            analyserRafRef.current = requestAnimationFrame(tick);
          };
          tick();

          // Mix mic + Lea into one MediaStream we can MediaRecorder.
          try {
            const dest = ctx.createMediaStreamDestination();
            recorderDestRef.current = dest;
            remoteSource.connect(dest);
            if (localStreamRef.current) {
              const micSource = ctx.createMediaStreamSource(
                localStreamRef.current,
              );
              micSource.connect(dest);
              micSourceRef.current = micSource;
            }
            // Negotiate the container per browser instead of hardcoding webm
            // (which threw NotSupportedError on Safari/iOS and silently
            // disabled recording there).
            const mimeType = pickRecordingMimeType();
            const recorder = new MediaRecorder(
              dest.stream,
              mimeType ? { mimeType } : undefined,
            );
            recorderMimeRef.current =
              recorder.mimeType || mimeType || "audio/webm";
            recorderRef.current = recorder;
            recorderChunksRef.current = [];
            recorder.ondataavailable = (ev) => {
              if (ev.data.size > 0) recorderChunksRef.current.push(ev.data);
            };
            recorder.onerror = (ev) => {
              // eslint-disable-next-line no-console
              console.warn("[recorder] error mid-call:", ev);
            };
            recorder.start(2000);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[recorder] failed to start:", err);
          }
        } catch {
          /* AudioContext blocked — let the call proceed without level meter */
        }
      };

      // ── Mic interruption handling (iOS alarms / phone calls) ────────────
      // An alarm or incoming call MUTES the mic track (it stays "live") and
      // suspends the AudioContext — VAD goes deaf and the call looks frozen
      // while the connection stays up. Strategy: try to recover the mic with
      // a fresh getUserMedia + replaceTrack; if that fails, end-and-save via
      // markDead instead of hanging forever.
      const recoverMic = async (reason: string): Promise<void> => {
        if (deadRef.current) return;
        // eslint-disable-next-line no-console
        console.warn(`[mic] lost (${reason}) — attempting recovery`);
        try {
          const fresh =
            await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
          const newTrack = fresh.getAudioTracks()[0];
          if (!newTrack) throw new Error("no audio track in fresh stream");
          const sender = pc
            .getSenders()
            .find((s) => s.track === null || s.track.kind === "audio");
          if (!sender) throw new Error("no audio sender on the peer");
          await sender.replaceTrack(newTrack);

          localStreamRef.current?.getTracks().forEach((t) => t.stop());
          localStreamRef.current = fresh;
          attachMicGuards(fresh);

          // Rewire the recording mix to the fresh mic.
          const ctx = audioCtxRef.current;
          const dest = recorderDestRef.current;
          if (ctx && dest) {
            try {
              micSourceRef.current?.disconnect();
            } catch {
              /* already disconnected */
            }
            const src = ctx.createMediaStreamSource(fresh);
            src.connect(dest);
            micSourceRef.current = src;
          }
          if (audioCtxRef.current?.state === "suspended") {
            audioCtxRef.current.resume().catch(() => {});
          }
          // eslint-disable-next-line no-console
          console.info("[mic] recovered after interruption");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[mic] recovery failed:", err);
          markDead(`microphone lost (${reason})`);
        }
      };

      const attachMicGuards = (stream: MediaStream): void => {
        stream.getAudioTracks().forEach((track) => {
          // Track fully ended (permission revoked, device unplugged).
          track.onended = () => void recoverMic("track ended");
          // Track muted by the OS (alarm, call). Often unmutes by itself
          // when the interruption clears — give it a 10s grace first.
          track.onmute = () => {
            setTimeout(() => {
              if (
                !deadRef.current &&
                track.readyState === "live" &&
                track.muted
              ) {
                void recoverMic("track muted >10s");
              }
            }, 10_000);
          };
        });
      };

      const stream =
        await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      localStreamRef.current = stream;
      attachMicGuards(stream);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // Returning to the tab after an interruption: poke the suspended
      // AudioContext + the paused remote <audio> back to life.
      const onVisible = () => {
        if (document.visibilityState !== "visible") return;
        if (audioCtxRef.current?.state === "suspended") {
          audioCtxRef.current.resume().catch(() => {});
        }
        audioRef.current?.play().catch(() => {});
      };
      document.addEventListener("visibilitychange", onVisible);
      visibilityHandlerRef.current = onVisible;

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onmessage = (e: MessageEvent<string>) => handleServerEvent(e.data);
      dc.onopen = () => {
        // Ask the agent to take the first turn (greet on connect).
        dc.send(JSON.stringify({ type: "response.create" }));
      };
      // OpenAI closing the session closes the data channel even when ICE
      // stays up — treat it as a lost connection (cleanup suppresses this
      // for intentional teardowns via deadRef).
      dc.onclose = () => markDead("data channel closed");

      // ── Stall watchdog ────────────────────────────────────────────────────
      // Covers the "I answered and then nothing" failure: connection healthy,
      // but no response ever starts after the user's turn was committed.
      // After 10s of silence we nudge with response.create (cheap, idempotent
      // enough — an "active response" error is just ignored upstream); after
      // two failed nudges we declare the session dead so the page auto-ends
      // and the transcript is saved.
      watchdogRef.current = setInterval(() => {
        if (deadRef.current) return;
        // ── Conversation-idle re-engage ────────────────────────────────────
        // No pending response, nobody has spoken for 30s — the model closed
        // a turn without a question ("Thanks for that.") and stalled. Nudge
        // it to check in. Capped at 2 so a participant who walked away isn't
        // pestered forever.
        if (
          awaitingSinceRef.current === null &&
          lastActivityRef.current > 0 &&
          Date.now() - lastActivityRef.current > 30_000 &&
          idleNudgeCountRef.current < 2
        ) {
          idleNudgeCountRef.current += 1;
          lastActivityRef.current = Date.now();
          // eslint-disable-next-line no-console
          console.info(
            `[realtime] conversation idle 30s — re-engaging (${idleNudgeCountRef.current}/2)`,
          );
          try {
            dcRef.current?.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "message",
                  role: "system",
                  content: [
                    {
                      type: "input_text",
                      text: "The conversation has gone quiet. Gently check in with the participant and continue the interview with your next question.",
                    },
                  ],
                },
              }),
            );
            dcRef.current?.send(JSON.stringify({ type: "response.create" }));
          } catch {
            /* channel gone — connection-lost path handles it */
          }
        }

        const since = awaitingSinceRef.current;
        if (since === null) return;
        if (Date.now() - since < 10_000) return;
        if (nudgeCountRef.current >= 2) {
          markDead("agent unresponsive after 2 nudges");
          return;
        }
        nudgeCountRef.current += 1;
        awaitingSinceRef.current = Date.now();
        // eslint-disable-next-line no-console
        console.warn(
          `[realtime] no response ${10 * nudgeCountRef.current}s after user turn — nudging agent (${nudgeCountRef.current}/2)`,
        );
        try {
          dcRef.current?.send(JSON.stringify({ type: "response.create" }));
        } catch {
          /* channel gone — pc/dc handlers will mark dead */
        }
      }, 5_000);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`,
        {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
        },
      );
      if (!sdpRes.ok) {
        const body = await sdpRes.text();
        throw new Error(`sdp exchange failed: ${body}`);
      }
      const answer: RTCSessionDescriptionInit = {
        type: "answer",
        sdp: await sdpRes.text(),
      };
      await pc.setRemoteDescription(answer);

      // ── Duration cap (study.maxDurationMinutes, ≤ OpenAI's 60-min hard
      // limit). Soft wrap-up at T-60s: Lea gets a system instruction to
      // thank + say goodbye and a response.create to deliver it. Hard stop
      // at T-5s: `timeUp` flips and the page auto-ends — we beat OpenAI's
      // kill so the close is ours, not a dropped line.
      const capMinutes = Math.min(Math.max(maxDurationMinutes, 2), 60);
      const capMs = capMinutes * 60_000;
      wrapUpTimerRef.current = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.info(`[duration] ${capMinutes}min cap — sending wrap-up`);
        try {
          dcRef.current?.send(
            JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "message",
                role: "system",
                content: [{ type: "input_text", text: WRAP_UP_INSTRUCTION }],
              },
            }),
          );
          dcRef.current?.send(JSON.stringify({ type: "response.create" }));
        } catch {
          /* channel gone — connection-lost path handles it */
        }
      }, capMs - 60_000);
      hardStopTimerRef.current = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.info("[duration] cap reached — auto-ending");
        setTimeUp(true);
      }, capMs - 5_000);

      setStatus("connected");
    } catch (err) {
      cleanup();
      setError(err instanceof Error ? err.message : "Failed to start call");
      setStatus("error");
    }
  }, [audioRef, cleanup, studyId, handleServerEvent, maxDurationMinutes]);

  /** Returns the server validity status (e.g. "completed_valid" /
   *  "completed_invalid"), or undefined if the save failed — the caller uses
   *  it to decide reward eligibility on /complete. */
  const stop = useCallback(async (): Promise<string | undefined> => {
    const audioBlob = await finalizeRecording();
    cleanup();
    const status = await saveInterview(audioBlob);
    setStatus("ended");
    return status;
  }, [cleanup, finalizeRecording, saveInterview]);

  const pause = useCallback(() => {
    if (status !== "connected" || paused) return;
    // Disarm the stall watchdog — silence while paused is intentional.
    awaitingSinceRef.current = null;
    lastActivityRef.current = 0; // 0 = idle re-engage disarmed
    try {
      dcRef.current?.send(JSON.stringify({ type: "response.cancel" }));
    } catch {
      /* no active response */
    }
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((t) => (t.enabled = false));
    if (audioRef.current) audioRef.current.muted = true;
    pausedAtRef.current = Date.now();
    setPaused(true);
  }, [audioRef, paused, status]);

  const resume = useCallback(() => {
    if (!paused) return;
    if (pausedAtRef.current != null) {
      pausedTotalMsRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
    lastActivityRef.current = Date.now();
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((t) => (t.enabled = true));
    if (audioRef.current) audioRef.current.muted = false;
    setPaused(false);
  }, [audioRef, paused]);

  return {
    status,
    paused,
    error,
    level,
    assistantText,
    userSpeaking,
    connectionLost,
    timeUp,
    agentEnded,
    start,
    stop,
    pause,
    resume,
  };
}

// ── Usage parsing helpers ───────────────────────────────────────────────────

/** Subset of OpenAI Realtime's `response.usage` we read — kept narrow so we
 *  survive shape drift in fields we don't price. */
interface RealtimeUsage {
  input_token_details?: {
    text_tokens?: number;
    audio_tokens?: number;
    cached_tokens_details?: { text_tokens?: number; audio_tokens?: number };
  };
  output_token_details?: { text_tokens?: number; audio_tokens?: number };
}

/** Add one event's usage into the running aggregate. Subtracts cached counts
 *  from the headline input totals so we can charge cached vs. non-cached
 *  input tokens at separate rates. */
function accumulateUsage(acc: UsageAggregate, u: RealtimeUsage): void {
  const itd = u.input_token_details ?? {};
  const otd = u.output_token_details ?? {};
  const ctd = itd.cached_tokens_details ?? {};

  const cachedText = ctd.text_tokens ?? 0;
  const cachedAudio = ctd.audio_tokens ?? 0;
  const totalInputText = itd.text_tokens ?? 0;
  const totalInputAudio = itd.audio_tokens ?? 0;

  acc.events += 1;
  acc.input_text_tokens += Math.max(0, totalInputText - cachedText);
  acc.input_audio_tokens += Math.max(0, totalInputAudio - cachedAudio);
  acc.input_cached_text_tokens += cachedText;
  acc.input_cached_audio_tokens += cachedAudio;
  acc.output_text_tokens += otd.text_tokens ?? 0;
  acc.output_audio_tokens += otd.audio_tokens ?? 0;
}
